<template>
  <v-app :style="{ background: $vuetify.theme.currentTheme.background }" v-if="beforeMountComplete">
    <nav-bar />

    <v-main>
      <v-container class="pa-sm-6 pa-4 offsetApp">
        <router-view></router-view>

        <side-drawer />
      </v-container>
    </v-main>

    <global-footer />

    <report-popup />
    <snack-bar />
  </v-app>
</template>

<script>
import NavBar from "./components/the-navbar.vue";
import GlobalFooter from "./components/the-footer.vue";
import SnackBar from "./components/snackbar.vue";
import SideDrawer from "./components/side-drawer.vue";
import SessionTools from "./mixins/sessionTools";

import { getCookie } from "./util/cookieTools";
import ReportPopup from "./components/report-popup.vue";
import isMobileBuild from "./util/isMobileBuild";
import { Preferences } from "@capacitor/preferences";

const UNAUTHORIZED_ERROR_CODE = 401;

export default {
  name: "App",

  metaInfo: {
    title: "Telemonial",
    description: "Connect with your friends and write reviews about books, movies, podcasts, albums, and tv shows",
  },

  components: {
    ReportPopup,
    NavBar,
    GlobalFooter,
    SnackBar,
    SideDrawer,
  },

  mixins: [SessionTools],

  data: () => ({
    beforeMountComplete: false,
  }),

  async beforeMount() {
    console.log("Handling before mount");
    // Handle theme preferences
    // First, check user settings
    const theme = await this.getThemeFromStorage();
    if (theme) {
      this.$vuetify.theme.dark = theme === "dark";
    } else {
      // Fallback to system preferences
      const darkModeMatcher = "(prefers-color-scheme: dark)";
      this.$vuetify.theme.dark = window.matchMedia(darkModeMatcher).matches;

      // Watch for updates
      window.matchMedia(darkModeMatcher).addEventListener("change", ({ matches }) => {
        this.$vuetify.theme.dark = matches;
      });
    }

    // Check for login token
    let auth = getCookie("authorization");

    if (isMobileBuild()) {
      // For mobile, check if stored in app storage
      if (!auth) {
        auth = (
          await Preferences.get({
            key: "authorization",
          })
        ).value;
      }
    }

    if (auth) {
      console.log("Updating auth state");
      this.$store.commit("login", { authorization: auth });
      this.$axios.defaults.headers.common["Authorization"] = "Bearer " + auth;

      await this.updateLastOnline();
    }

    if (isMobileBuild()) {
      // https://ionicframework.com/docs/developing/hardware-back-button
      document.addEventListener("ionBackButton", (ev) => {
        ev.detail.register(1, () => {
          this.$router.back();
        });
      });
    }

    this.beforeMountComplete = true;
  },

  methods: {
    updateLastOnline: async function () {
      try {
        await this.$axios.post("/analytics/last-online");
      } catch (error) {
        if (error.response.status === UNAUTHORIZED_ERROR_CODE) {
          this.logout();
        }
      }
    },
  },
};
</script>

<style>
/* Variables */
:root {
  /* Colors */
  --text-inverted: white;
  --semi-opaque-overlay-bg-color: rgba(0, 0, 0, 0.6);
  --semi-opaque-overlay-bg-color-darker: rgba(0, 0, 0, 0.75);
  --card-bg-dark: #3c3c43;
  /* Sizing */
  --breakpoint-md: 960px;
  --breakpoint-xs: 600px;
  /* Font */
  --default-paragraph-line-height: 1.3;
}

* {
  box-sizing: border-box;
}

.container {
  max-width: 100% !important;
}

.text-h1 {
  font-weight: 700 !important;
}
.text-h2 {
  font-weight: 700 !important;
}
.text-h3 {
  font-weight: 600 !important;
}
.text-h4 {
  font-weight: 600 !important;
}
.text-h5 {
  font-weight: 500 !important;
}

.offsetApp {
  margin-top: max(64px, calc(env(safe-area-inset-top) + 60px));
}

/*--------------------------
   START VUETIFY OVERRIDES
------------------------- */

/* Following code should fix invisible cursor bug for v-text-fields
found at https://github.com/vuetifyjs/vuetify/issues/13628
no clue why it works*/
.v-text-field > .v-input__control > .v-input__slot {
  transition: background 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

/* Global style rules */
.v-menu__content .v-list.utility-menu .v-list-item:hover {
  cursor: pointer;
}
.theme--light {
  .v-menu__content .v-list.utility-menu .v-list-item:hover {
    background-color: var(--v-card-darken1);
  }
}
.theme--dark {
  .v-menu__content .v-list.utility-menu .v-list-item:hover {
    background-color: var(--v-card-lighten1);
  }
}
.v-snack__wrapper.theme--dark {
  background-color: var(--card-bg-dark);
}

.theme--dark.v-list,
.theme--light.vlist,
.theme--dark.v-card,
.theme--light.v-card {
  background-color: var(--v-card-base);
}

.theme--dark.v-expansion-panels .v-expansion-panel,
.theme--light.v-expansion-panels .v-expansion-panel {
  background-color: var(--v-background-base);
}

.v-btn--outlined {
  border: 3px solid;
}

/*--------------------------
   END VUETIFY OVERRIDES
------------------------- */

/* From https://www.reddit.com/r/web_design/comments/z4k3kb/substituting_an_h1_with_an_image/ */
.visually-hidden {
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

.button-text-gray-underlined {
  text-decoration: underline;
  color: var(--v-text-lighten1);
}

.spoiler-censor {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(3px);
  width: 100%;
  height: 100%;
}

h1.page-title {
  font-size: 18px;
  line-height: 1;
  font-weight: bold;
  text-align: center;
  padding-bottom: 6px;
}

.filter-wrapper {
  width: 130px;
  margin-right: 8px;
}

.menu-button {
  border-radius: 16px;
  transition: 0.2s;
  padding: 4px;
  line-height: 1;
}
.theme--dark {
  .menu-button:hover {
    background-color: var(--v-card-lighten1);
  }
  .menu-button:focus {
    background-color: var(--v-card-lighten2);
  }
}
.theme--light {
  .menu-button:hover {
    background-color: var(--v-card-darken1);
  }
  .menu-button:focus {
    background-color: var(--v-card-darken2);
  }
}

.no-items-found-message {
  text-align: center;
  color: var(--v-text-lighten2);
  font-size: 26px;
}

.bg-card {
  background-color: var(--v-card-base) !important;
}

.legacy-review-tile.v-card {
  margin: auto;

  cursor: pointer;

  overflow: hidden;

  transition: box-shadow 0.25s ease-out 0.1s;
}

/* Icon detailing the medium of media i.e. film, album, etc. */
.legacy-review-tile.v-card .overlay-icon {
  position: absolute !important;
  margin: 8px;
}
.legacy-review-tile .overlay-icon.medium-icon {
  top: 0;
  left: 0;
}
.legacy-review-tile .overlay-icon.rating-circle {
  top: 0;
  right: 0;
}
</style>
